import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	type Attempt,
	type ChatMessage,
	isCorrect,
	type ReviewQuestion,
	resultLabel,
} from "../lib/review";

type Props = {
	current: ReviewQuestion;
	attempt: Attempt;
	questionMessages: ChatMessage[];
	explanation: string;
	chatInput: string;
	isExplaining: boolean;
	isChatting: boolean;
	chatError: string;
	currentIndex: number;
	questionCount: number;
	onExplain: () => void;
	onNext: () => void;
	onChatInputChange: (value: string) => void;
	onSendMessage: () => void;
};

export function FeedbackCard({
	current,
	attempt,
	questionMessages,
	explanation,
	chatInput,
	isExplaining,
	isChatting,
	chatError,
	currentIndex,
	questionCount,
	onExplain,
	onNext,
	onChatInputChange,
	onSendMessage,
}: Props) {
	const feedbackCardRef = useRef<HTMLElement>(null);

	useEffect(() => {
		const frame = window.requestAnimationFrame(() => {
			feedbackCardRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		});

		return () => window.cancelAnimationFrame(frame);
	}, []);

	return (
		<section
			ref={feedbackCardRef}
			className={`feedback-card ${attempt.status}`}
			aria-live="polite"
		>
			<div className="feedback-heading">
				<span className="feedback-icon" aria-hidden="true">
					{isCorrect(attempt.status) ? "✓" : "↺"}
				</span>
				<div>
					<p className="eyebrow">ANSWER REVIEW</p>
					<h2>{resultLabel(attempt.status)}</h2>
				</div>
			</div>
			<p className="feedback-copy">
				{current.question.explanation}
				{attempt.feedback && (
					<>
						<br />
						{attempt.feedback}
					</>
				)}
			</p>

			{attempt.misconceptionTags && attempt.misconceptionTags.length > 0 && (
				<div className="misconception-tags">
					<span>Focus areas</span>
					<div>
						{attempt.misconceptionTags.map((tag) => (
							<span className="misconception-tag" key={tag}>
								{tag}
							</span>
						))}
					</div>
				</div>
			)}
			<div className="post-actions">
				<button
					className="secondary-button"
					type="button"
					onClick={onExplain}
					disabled={isExplaining}
				>
					{isExplaining ? "Preparing explanation…" : "Further explain"}
				</button>
				<button className="next-button" type="button" onClick={onNext}>
					{currentIndex === questionCount - 1
						? "Finish review"
						: "Next question"}{" "}
					<span aria-hidden="true">→</span>
				</button>
			</div>
			{explanation && (
				<div className="assistant-response">
					<span aria-hidden="true">✦</span>
					<div className="markdown-content">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>
							{explanation}
						</ReactMarkdown>
					</div>
				</div>
			)}
			<div className="chat-area">
				<label htmlFor="chat-message">Discuss this question</label>
				<p>
					You can ask why an option is wrong or request another example from
					this lesson.
				</p>
				{questionMessages.map((message) => (
					<div
						className={`chat-message ${message.role}`}
						key={`${message.role}-${message.text}`}
					>
						<div className="markdown-content">
							<ReactMarkdown remarkPlugins={[remarkGfm]}>
								{message.text}
							</ReactMarkdown>
						</div>
					</div>
				))}
				{isChatting && (
					<p className="chat-status" role="status">
						Thinking…
					</p>
				)}
				{chatError && (
					<div className="chat-error" role="alert">
						<span>{chatError}</span>
						<button type="button" onClick={onSendMessage} disabled={isChatting}>
							Retry
						</button>
					</div>
				)}
				<div className="chat-composer">
					<input
						id="chat-message"
						value={chatInput}
						onChange={(event) => onChatInputChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") onSendMessage();
						}}
						placeholder="Ask a follow-up question"
						maxLength={2000}
						disabled={isChatting}
					/>
					<button
						type="button"
						onClick={onSendMessage}
						disabled={!chatInput.trim() || isChatting}
					>
						{isChatting ? "Sending…" : "Send"}
					</button>
				</div>
			</div>
		</section>
	);
}
